import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    MenuItem,
    IconButton,
} from "@mui/material";

import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { db, storage } from "../firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";

import Menu from "../panel/Menu";

/* ---------------- CONSTANTS ---------------- */

const INDIAN_STATES = [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa",
    "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
    "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
    "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
    "Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir",
    "Ladakh","Puducherry","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
    "Lakshadweep","Andaman and Nicobar Islands"
];

const PROPERTY_TYPES = ["Hotel", "Bhaktniwas", "Lodge", "Resort", "Villa"];

/* ---------------- COMPONENT ---------------- */

function Property() {
    const [propertyId, setPropertyId] = useState(null);
    const [property, setProperty] = useState({});
    const [editData, setEditData] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingImage, setLoadingImage] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [message, setMessage] = useState("");

    /* ---------------- LOAD PROPERTY ---------------- */

    useEffect(() => {
        const loadProperty = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) return;

            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (!roleSnap.exists()) return;

            const pid = roleSnap.data().propertyId;
            setPropertyId(pid);

            const propSnap = await getDoc(doc(db, "properties", pid));
            if (propSnap.exists()) {
                setProperty(propSnap.data());
                setEditData({ ...propSnap.data(), country: "India" });
            }
            setLoading(false);
        };

        loadProperty();
    }, []);

    /* ---------------- IMAGE UPLOAD ---------------- */

    const uploadImage = async (file, field) => {
        if (!file || !propertyId) return;

        try {
            setLoadingImage(true);

            const storageRef = ref(
                storage,
                `property/${propertyId}/${field}`
            );

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            // cache busting
            const finalUrl = `${url}?t=${Date.now()}`;

            await updateDoc(doc(db, "properties", propertyId), {
                [field]: finalUrl,
            });

            setProperty(prev => ({ ...prev, [field]: finalUrl }));
            setEditData(prev => ({ ...prev, [field]: finalUrl }));
            setMessage("Image updated successfully!");

        } catch (err) {
            console.error(err);
            setMessage("Image upload failed!");
        } finally {
            setLoadingImage(false);
        }
    };

    /* ---------------- SAVE DETAILS ---------------- */

    const saveDetails = async () => {
        try {
            setSaving(true);
            await updateDoc(doc(db, "properties", propertyId), editData);
            setProperty(editData);
            setMessage("Profile updated successfully!");
            setOpenEdit(false);
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Typography sx={{ p: 4 }}>Loading...</Typography>;

    return (
        <>
            <Menu />

            <Box className="content-wrapper" sx={{ p: 2, background: "#f5f5f5" }}>
                {message && <Alert sx={{ mb: 2 }}>{message}</Alert>}

                {(loadingImage || saving) && (
                    <Box sx={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(255,255,255,0.6)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                        <CircularProgress size={70} />
                    </Box>
                )}

                {/* COVER IMAGE */}
                <Box sx={{ height: 350, position: "relative", borderRadius: 3, overflow: "hidden" }}>
                    <img
                        src={property.buildingImage || "https://via.placeholder.com/1200x400"}
                        alt="cover"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />

                    <Button
                        component="label"
                        startIcon={<CameraAltIcon />}
                        sx={{ position: "absolute", bottom: 20, right: 20, bgcolor: "#fff" }}
                    >
                        Change Cover
                        <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                                uploadImage(e.target.files[0], "buildingImage")
                            }
                        />
                    </Button>
                </Box>

                {/* PROFILE CARD */}
                <Paper sx={{ width: "70%", mx: "auto", mt: -10, p: 4, borderRadius: 4 }}>
                    <Box sx={{ position: "relative", width: 120, mx: "auto", mt: -8 }}>
                        <IconButton
                            component="label"
                            sx={{ p: 0 }}
                        >
                            <Avatar
                                src={property.logo || ""}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    border: "4px solid #fff",
                                }}
                            />
                            <Box sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                bgcolor: "#1976d2",
                                borderRadius: "50%",
                                p: 0.5,
                            }}>
                                <CameraAltIcon sx={{ color: "#fff", fontSize: 18 }} />
                            </Box>
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                    uploadImage(e.target.files[0], "logo")
                                }
                            />
                        </IconButton>
                    </Box>

                    <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Typography variant="h5" fontWeight={700}>
                            {property.type} - {property.hotelname}
                        </Typography>

                        <Typography>
                            {property.address}, {property.city}, {property.state} - {property.pincode}
                        </Typography>
                        <Typography>{property.mobile}</Typography>
                        <Typography>{property.email}</Typography>
                        {property.gstNo && (
                            <Typography sx={{ fontWeight: 500, color: "text.secondary", mt: 0.5 }}>
                                GST No: {property.gstNo}
                            </Typography>
                        )}

                        <Button
                            startIcon={<EditIcon />}
                            sx={{ mt: 2 }}
                            variant="contained"
                            onClick={() => setOpenEdit(true)}
                        >
                            Edit Profile
                        </Button>
                    </Box>

                    {/* OFFICIAL STAMP SECTION */}
                    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e0e0e0", textAlign: "center" }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            Official Property Stamp
                        </Typography>
                        <Box sx={{ maxWidth: 200, mx: "auto" }}>
                            <Box sx={{ height: 100, border: "1px dashed #ccc", display: "flex", alignItems: "center", justifyContent: "center", mb: 1, borderRadius: 2, overflow: "hidden" }}>
                                {property.stamp ? (
                                    <img src={property.stamp} alt="Stamp" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                                ) : (
                                    <Typography variant="caption" color="textSecondary">No Stamp Uploaded</Typography>
                                )}
                            </Box>
                            <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                                Upload Stamp
                                <input hidden type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], "stamp")} />
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* EDIT DIALOG */}
                <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth>
                    <DialogTitle>Edit Property</DialogTitle>
                    <DialogContent>
                        <TextField label="Hotel Name" fullWidth size="small" margin="dense"
                            value={editData.hotelname || ""}
                            onChange={(e) => setEditData({ ...editData, hotelname: e.target.value })}
                        />

                        <TextField label="Email" fullWidth size="small" margin="dense"
                            value={editData.email || ""}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        />

                        <TextField label="Mobile" fullWidth size="small" margin="dense"
                            value={editData.mobile || ""}
                            onChange={(e) => setEditData({ ...editData, mobile: e.target.value })}
                        />

                        <TextField label="GST Number" fullWidth size="small" margin="dense"
                            value={editData.gstNo || ""}
                            onChange={(e) => setEditData({ ...editData, gstNo: e.target.value })}
                        />

                        <TextField label="Address" fullWidth size="small" margin="dense"
                            value={editData.address || ""}
                            onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                        />

                        <TextField label="City" fullWidth size="small" margin="dense"
                            value={editData.city || ""}
                            onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                        />

                        <TextField select label="State" fullWidth size="small" margin="dense"
                            value={editData.state || ""}
                            onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                        >
                            {INDIAN_STATES.map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </TextField>

                        <TextField label="Country" fullWidth size="small" margin="dense" value="India" disabled />

                        <TextField label="Pincode" fullWidth size="small" margin="dense"
                            value={editData.pincode || ""}
                            onChange={(e) => setEditData({ ...editData, pincode: e.target.value })}
                        />

                        <TextField select label="Property Type" fullWidth size="small" margin="dense"
                            value={editData.type || ""}
                            onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                        >
                            {PROPERTY_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                        <Button variant="contained" onClick={saveDetails}>Save</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}

export default Property;